import React, { useEffect, useRef } from 'react';
import { EditorContent } from '@tiptap/react';
import useTiptapEditor from '../../hooks/useTiptapEditor';

/**
 * Rich text editor component using Tiptap
 * @param {string} value - Initial content for the editor
 * @param {function} onChange - Callback function when content changes
 */
const TiptapEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  
  const {
    editor,
    isActive,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setParagraph,
    toggleHeading,
    toggleBulletList,
    toggleOrderedList,
    setTextAlign,
  } = useTiptapEditor(value, onChange);

  // Function to adjust editor height based on content
  const adjustEditorHeight = () => {
    if (editorRef.current) {
      const editorElement = editorRef.current.querySelector('.ProseMirror');
      if (editorElement) {
        // Reset height to auto to get accurate scrollHeight
        editorElement.style.height = 'auto';
        
        // Calculate content height (with minimum of 200px)
        const contentHeight = Math.max(200, editorElement.scrollHeight);
        
        // Set the new height
        editorElement.style.height = `${contentHeight}px`;
      }
    }
  };

  // Adjust height when editor or content changes
  useEffect(() => {
    if (editor) {
      // Add a small delay to ensure content is rendered
      const timeoutId = setTimeout(() => {
        adjustEditorHeight();
      }, 100);

      // Set up observer for content changes
      const observer = new MutationObserver(() => {
        adjustEditorHeight();
      });

      // Start observing the editor content
      const editorElement = editorRef.current?.querySelector('.ProseMirror');
      if (editorElement) {
        observer.observe(editorElement, { 
          childList: true, 
          subtree: true, 
          characterData: true 
        });
      }

      // Clean up
      return () => {
        clearTimeout(timeoutId);
        observer.disconnect();
      };
    }
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-wrapper" ref={editorRef}>
      <div className="tiptap-toolbar">
        <button
          onClick={toggleBold}
          className={isActive.bold ? 'is-active' : ''}
          type="button"
        >
          Bold
        </button>
        <button
          onClick={toggleItalic}
          className={isActive.italic ? 'is-active' : ''}
          type="button"
        >
          Italic
        </button>
        <button
          onClick={toggleUnderline}
          className={isActive.underline ? 'is-active' : ''}
          type="button"
        >
          Underline
        </button>
        <button
          onClick={setParagraph}
          className={isActive.paragraph ? 'is-active' : ''}
          type="button"
        >
          Paragraph
        </button>
        <button
          onClick={() => toggleHeading(1)}
          className={isActive.heading1 ? 'is-active' : ''}
          type="button"
        >
          H1
        </button>
        <button
          onClick={() => toggleHeading(2)}
          className={isActive.heading2 ? 'is-active' : ''}
          type="button"
        >
          H2
        </button>
        <button
          onClick={() => toggleHeading(3)}
          className={isActive.heading3 ? 'is-active' : ''}
          type="button"
        >
          H3
        </button>
        <button
          onClick={toggleBulletList}
          className={isActive.bulletList ? 'is-active' : ''}
          type="button"
        >
          Bullet List
        </button>
        <button
          onClick={toggleOrderedList}
          className={isActive.orderedList ? 'is-active' : ''}
          type="button"
        >
          Ordered List
        </button>
        <button
          onClick={() => setTextAlign('left')}
          className={isActive.alignLeft ? 'is-active' : ''}
          type="button"
        >
          Left
        </button>
        <button
          onClick={() => setTextAlign('center')}
          className={isActive.alignCenter ? 'is-active' : ''}
          type="button"
        >
          Center
        </button>
        <button
          onClick={() => setTextAlign('right')}
          className={isActive.alignRight ? 'is-active' : ''}
          type="button"
        >
          Right
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default TiptapEditor;
